import { getCurrencySymbol } from '@src/utilities/currency';
import React from 'react';

interface Milestone {
  value: number; // milestone amount, e.g., 5000
  percent: number; // percent reward at this milestone
}

interface ProgressWithMilestoneProps {
  current: number; // current user value, e.g., 2000
  max: number; // maximum value, e.g., 10000
  milestones: Milestone[];
  className?: string;
}

export function ProgressWithMilestone({
  current,
  max,
  milestones,
  className = '',
}: ProgressWithMilestoneProps) {
  const progressPercent = (current / max) * 100;
  const gradient =
    'linear-gradient(90deg, #FDC52C 0%, #F19E44 50%, #FD5B2C 100%)';

  return (
    <div className={`relative w-full h-16 ${className}`}>
      {/* Background bar */}
      <div className='absolute w-full h-4 bg-gray-200 rounded-full top-1/2 transform -translate-y-1/2'></div>

      {/* Active gradient bar */}
      <div
        className='absolute h-4 rounded-full top-1/2 transform -translate-y-1/2'
        style={{
          width: `${progressPercent}%`,
          background: gradient,
          borderRadius: '9999px',
        }}
      ></div>

      {/* Milestones */}
      {milestones.map((milestone, index) => {
        const isActive = current >= milestone.value;

        return (
          <div
            key={index}
            className='absolute flex flex-col items-center'
            style={{
              left: `calc(${(milestone.value / max) * 100}% - 1rem)`, // subtract half of outer circle width
            }}
          >
            {/* Outer white circle */}
            <div className='w-10 h-10 mt-3 rounded-full bg-white flex items-center justify-center'>
              {/* Inner circle with gradient or gray */}
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  isActive ? '' : 'bg-gray-200'
                }`}
                style={isActive ? { background: gradient } : undefined}
              >
                <span
                  className={`text-xs font-semibold ${
                    isActive ? 'text-black' : 'text-gray-500'
                  }`}
                >
                  {milestone.percent}%
                </span>
              </div>
            </div>

            {/* Amount below */}
            <span
              className={`text-xs font-medium ${
                isActive
                  ? 'bg-gradient-to-r from-[#FDC52C] via-[#F19E44] to-[#FD5B2C] text-transparent bg-clip-text'
                  : 'text-gray-500'
              }`}
            >
              {getCurrencySymbol('EUR')}
              {milestone.value.toLocaleString()}
            </span>
          </div>
        );
      })}
    </div>
  );
}
